/**
 * Candle aggregation: M1 bars → M5/M15/H1/H4/D1.
 *
 * `history` stores M1 bars and serves them by range (docs/10 §1). The terminal
 * aggregates them for the other intervals, and this is **display aggregation of
 * server numbers**: open = first bar's open, close = last bar's close, high and
 * low are the extreme *server* values, compared as integers. Nothing is
 * recomputed, averaged or rounded — a bar drawn here is one of the M1 prices the
 * server produced, or nothing.
 *
 * Bucket boundaries come from the caller (`bucketOf`), so this module never
 * touches a clock and stays inside the no-float gate.
 */

import type { MinuteBar } from '../data/types.js';

export interface Candle {
  /** Bucket start, canonical ISO (UTC), stable across reloads. */
  readonly bucket: string;
  readonly open_e8: string;
  readonly high_e8: string;
  readonly low_e8: string;
  readonly close_e8: string;
  /** Ticks that formed the bucket, summed exactly (a count, as a string). */
  readonly ticks: string;
}

/**
 * Fold M1 bars into candles for one bucket width.
 *
 * Bars must be ascending; they are sorted defensively because a chart drawn from
 * out-of-order input is a silent lie about the market. The first and last bar of
 * each bucket are found by time, not by array index after sorting an already
 * sorted array — the sort is the one place order is established.
 */
export function aggregateBars(
  bars: readonly MinuteBar[],
  bucketOf: (iso: string) => string,
): Candle[] {
  if (bars.length === 0) return [];
  const ordered = [...bars].sort((left, right) =>
    left.minute_ts < right.minute_ts ? -1 : left.minute_ts > right.minute_ts ? 1 : 0,
  );

  const candles: Candle[] = [];
  let current: {
    bucket: string;
    open: string;
    high: string;
    low: string;
    close: string;
    ticks: bigint;
  } | null = null;

  for (const bar of ordered) {
    const bucket = bucketOf(bar.minute_ts);
    const ticks = BigInt(bar.tick_count);
    if (current === null || current.bucket !== bucket) {
      if (current !== null) {
        candles.push({
          bucket: current.bucket,
          open_e8: current.open,
          high_e8: current.high,
          low_e8: current.low,
          close_e8: current.close,
          ticks: current.ticks.toString(),
        });
      }
      current = {
        bucket,
        open: bar.open_e8,
        high: bar.high_e8,
        low: bar.low_e8,
        close: bar.close_e8,
        ticks,
      };
      continue;
    }
    if (BigInt(bar.high_e8) > BigInt(current.high)) current.high = bar.high_e8;
    if (BigInt(bar.low_e8) < BigInt(current.low)) current.low = bar.low_e8;
    current.close = bar.close_e8;
    current.ticks += ticks;
  }

  if (current !== null) {
    candles.push({
      bucket: current.bucket,
      open_e8: current.open,
      high_e8: current.high,
      low_e8: current.low,
      close_e8: current.close,
      ticks: current.ticks.toString(),
    });
  }
  return candles;
}

/**
 * Merge a live quote into the newest candle.
 *
 * The chart must move between history polls without inventing a bar: the current
 * bucket's close becomes the live price and its high/low widen to include it.
 * `null` when the quote's bucket is newer than the last candle (a new bar the
 * history service has not stored yet) — the caller then refetches instead of
 * fabricating an open.
 */
export function applyLivePrice(
  candles: readonly Candle[],
  bucket: string,
  priceE8: string,
): Candle[] | null {
  const last = candles[candles.length - 1];
  if (last === undefined) return null;
  if (last.bucket > bucket) return [...candles];
  if (last.bucket < bucket) return null;
  const high = BigInt(priceE8) > BigInt(last.high_e8) ? priceE8 : last.high_e8;
  const low = BigInt(priceE8) < BigInt(last.low_e8) ? priceE8 : last.low_e8;
  const updated: Candle = {
    bucket: last.bucket,
    open_e8: last.open_e8,
    high_e8: high,
    low_e8: low,
    close_e8: priceE8,
    // A live tick is one more tick in the bucket; the histogram must move with
    // the price or it would look frozen between history polls.
    ticks: (BigInt(last.ticks) + 1n).toString(),
  };
  return [...candles.slice(0, -1), updated];
}
